import React, { useState, useEffect, useContext, useMemo } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  InputAdornment,
  Modal,
  Backdrop,
  Fade,
  Chip,
  Stack,
  IconButton,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { AppContext } from "../../context/AppContext";
import SidebarMenu from "./AdminPanel/SidebarMenu";
import TopBar from "./AdminPanel/TopBar";
import { useNavigate } from "react-router-dom";

const normalize = (text) =>
  String(text || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const sortByName = (items = []) =>
  [...items].sort((a, b) =>
    String(a?.name || "").localeCompare(String(b?.name || ""), "es", {
      sensitivity: "base",
      numeric: true,
    })
  );

const headCellSx = {
  bgcolor: "#1565C0",
  color: "#fff",
  fontWeight: 700,
  whiteSpace: "nowrap",
  py: 1.15,
  fontSize: 14,
};

const AdminSection = () => {
  const navigate = useNavigate();
  const { sections, brands } = useContext(AppContext);

  const [open, setOpen] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(false);

  const [searchTermSections, setSearchTermSections] = useState("");
  const [pageSections, setPageSections] = useState(0);
  const [rowsPerPageSections, setRowsPerPageSections] = useState(10);

  const [searchTermBrands, setSearchTermBrands] = useState("");
  const [pageBrands, setPageBrands] = useState(0);
  const [rowsPerPageBrands, setRowsPerPageBrands] = useState(10);

  const [openImageModal, setOpenImageModal] = useState(false);
  const [modalImageUrl, setModalImageUrl] = useState("");
  const [modalImageTitle, setModalImageTitle] = useState("");

  const formattedSections = useMemo(() => {
    return sortByName(
      (sections || []).map((section) => ({
        id: section.id,
        name: section.name,
        image: section.image || null,
        categories: sortByName(section.categories || []).map((cat) => cat.name),
      }))
    );
  }, [sections]);

  const filteredSections = useMemo(() => {
    const term = normalize(searchTermSections);

    return formattedSections.filter((section) => {
      const categoriesText = section.categories.join(" ");
      return (
        !term ||
        normalize(section.name).includes(term) ||
        normalize(categoriesText).includes(term)
      );
    });
  }, [formattedSections, searchTermSections]);

  const filteredBrands = useMemo(() => {
    const term = normalize(searchTermBrands);

    return sortByName(brands || []).filter((brand) =>
      normalize(brand.name).includes(term)
    );
  }, [brands, searchTermBrands]);

  useEffect(() => {
    setPageSections(0);
  }, [searchTermSections]);

  useEffect(() => {
    setPageBrands(0);
  }, [searchTermBrands]);

  const handleChangePageSections = (event, newPage) => setPageSections(newPage);

  const handleChangeRowsPerPageSections = (event) => {
    setRowsPerPageSections(parseInt(event.target.value, 10));
    setPageSections(0);
  };

  const handleChangePageBrands = (event, newPage) => setPageBrands(newPage);

  const handleChangeRowsPerPageBrands = (event) => {
    setRowsPerPageBrands(parseInt(event.target.value, 10));
    setPageBrands(0);
  };

  const handleOpenImageModal = (url, title) => {
    setModalImageUrl(url);
    setModalImageTitle(title || "Imagen");
    setOpenImageModal(true);
  };

  const handleCloseImageModal = () => {
    setOpenImageModal(false);
    setModalImageUrl("");
    setModalImageTitle("");
  };

  const handleLogout = async () => {
    try {
      await fetch("/sistema/logout/", { method: "POST", credentials: "include" });
    } catch {}
    localStorage.clear();
    navigate("/login");
  };

  const handleSelect = (component) => {
    if (component === "AdminSection") navigate("/admin-panel/secciones");
    else if (component === "ModalInventario") setSubmenuOpen((s) => !s);
    else if (component === "AdminProduct") navigate("/admin-panel/inventario/productos");
    else if (component === "AdminVariant") navigate("/admin-panel/inventario/variantes");
    else if (component === "AdminProforma") navigate("/admin-panel/proformas");
    else if (component === "AdminClient") navigate("/admin-panel/clientes");
    else if (component === "AdminServices") navigate("/admin-panel/servicios");
    else if (component === "AdminPromotion") navigate("/admin-panel/promociones");
    else navigate("/admin-panel");
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f5f6fa" }}>
      <SidebarMenu
        open={open}
        setOpen={setOpen}
        submenuOpen={submenuOpen}
        setSubmenuOpen={setSubmenuOpen}
        navigate={navigate}
        handleLogout={handleLogout}
        handleSelect={handleSelect}
      />

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <TopBar open={open} setOpen={setOpen} />

        <Box sx={{ width: "95%", mx: "auto", mt: 3, mb: 5 }}>
          <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 3 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate("/admin-panel/secciones/nueva")}
              sx={{
                textTransform: "none",
                bgcolor: "#1565C0",
                "&:hover": { bgcolor: "#0D47A1" },
                borderRadius: 2,
                px: 2.2,
                fontWeight: 700,
                boxShadow: "0 8px 20px rgba(21,101,192,0.22)",
              }}
            >
              Administrar
            </Button>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} lg={8}>
              <Paper
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  boxShadow: "0 10px 30px rgba(15,23,42,0.06)",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: { xs: "flex-start", md: "center" },
                    justifyContent: "space-between",
                    gap: 2,
                    mb: 2,
                    flexWrap: "wrap",
                  }}
                >
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: 19, color: "#0f172a" }}>
                      Secciones y categorías
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: "#64748b", mt: 0.4 }}>
                      Organiza visualmente tu inventario por sección y categorías
                    </Typography>
                  </Box>

                  <Chip
                    label={`${filteredSections.length} secciones`}
                    color="info"
                    variant="outlined"
                    sx={{ fontWeight: 700, borderRadius: 2 }}
                  />
                </Box>

                <TextField
                  fullWidth
                  placeholder="Buscar por sección o categoría"
                  value={searchTermSections}
                  onChange={(e) => setSearchTermSections(e.target.value)}
                  sx={{ mb: 2.2 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />

                <TableContainer
                  sx={{
                    borderRadius: 2,
                    border: "1px solid #e2e8f0",
                    overflowX: "auto",
                    overflowY: "hidden",
                    maxWidth: "100%",
                    "&::-webkit-scrollbar": { height: 10 },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "#cbd5e1",
                      borderRadius: 10,
                    },
                  }}
                >
                  <Table size="small" sx={{ minWidth: 860 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ ...headCellSx, minWidth: 290 }}>
                          Sección
                        </TableCell>
                        <TableCell sx={{ ...headCellSx, minWidth: 380 }}>
                          Categorías
                        </TableCell>
                        <TableCell sx={{ ...headCellSx, minWidth: 120 }} align="center">
                          Imágenes
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {filteredSections
                        .slice(
                          pageSections * rowsPerPageSections,
                          pageSections * rowsPerPageSections + rowsPerPageSections
                        )
                        .map((section) => (
                          <TableRow
                            key={section.id}
                            hover
                            sx={{
                              "& td": { py: 1.1, borderBottom: "1px solid #edf2f7" },
                              "&:hover": { bgcolor: "#fafcff" },
                            }}
                          >
                            <TableCell sx={{ minWidth: 290 }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                                <Box
                                  sx={{
                                    width: 46,
                                    height: 46,
                                    borderRadius: 2,
                                    flexShrink: 0,
                                    border: "1px solid #dbeafe",
                                    bgcolor: section.image ? "#eef6ff" : "#f8fafc",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    overflow: "hidden",
                                  }}
                                >
                                  {section.image ? (
                                    <Box
                                      component="img"
                                      src={section.image}
                                      alt={section.name}
                                      sx={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                      }}
                                    />
                                  ) : (
                                    <FolderOpenOutlinedIcon sx={{ color: "#94a3b8", fontSize: 22 }} />
                                  )}
                                </Box>

                                <Box sx={{ minWidth: 0 }}>
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                    <Typography
                                      sx={{
                                        fontSize: 13.5,
                                        fontWeight: 700,
                                        color: "#0f172a",
                                      }}
                                    >
                                      {section.name}
                                    </Typography>

                                    {section.image && (
                                      <Button
                                        size="small"
                                        variant="text"
                                        startIcon={<VisibilityOutlinedIcon sx={{ fontSize: 16 }} />}
                                        onClick={() => handleOpenImageModal(section.image, section.name)}
                                        sx={{
                                          minWidth: "auto",
                                          px: 0.8,
                                          py: 0.2,
                                          textTransform: "none",
                                          fontWeight: 700,
                                          color: "#1565C0",
                                        }}
                                      >
                                        Ver imagen
                                      </Button>
                                    )}
                                  </Box>

                                  <Typography sx={{ fontSize: 11, color: "#64748b", mt: 0.3 }}>
                                    {section.categories.length}{" "}
                                    {section.categories.length === 1 ? "categoría" : "categorías"}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>

                            <TableCell sx={{ minWidth: 380 }}>
                              {section.categories.length > 0 ? (
                                <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap">
                                  {section.categories.map((category, index) => (
                                    <Chip
                                      key={`${section.id}-${category}-${index}`}
                                      icon={<LabelOutlinedIcon sx={{ fontSize: 15 }} />}
                                      label={category}
                                      size="small"
                                      variant="outlined"
                                      sx={{
                                        borderRadius: 2,
                                        fontWeight: 600,
                                        "& .MuiChip-label": { px: 1, fontSize: 11.5 },
                                      }}
                                    />
                                  ))}
                                </Stack>
                              ) : (
                                <Typography sx={{ fontSize: 12.5, color: "#94a3b8", fontWeight: 600 }}>
                                  Sin categorías
                                </Typography>
                              )}
                            </TableCell>

                            <TableCell align="center" sx={{ minWidth: 120 }}>
                              {section.image ? (
                                <Tooltip title="Ver imagen" arrow>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleOpenImageModal(section.image, section.name)}
                                    sx={{
                                      color: "#1565C0",
                                      borderRadius: 2,
                                      border: "1px solid #dbeafe",
                                      bgcolor: "#f8fbff",
                                      "&:hover": { bgcolor: "#eef6ff" },
                                    }}
                                  >
                                    <ImageOutlinedIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Tooltip>
                              ) : (
                                <Typography sx={{ fontSize: 12, color: "#94a3b8", fontWeight: 700 }}>
                                  -
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}

                      {!filteredSections.length && (
                        <TableRow>
                          <TableCell colSpan={3} align="center" sx={{ py: 5 }}>
                            <Stack spacing={0.75} alignItems="center">
                              <Typography sx={{ fontWeight: 700, color: "#0f172a" }}>
                                No hay secciones para mostrar
                              </Typography>
                              <Typography sx={{ fontSize: 13, color: "#64748b" }}>
                                Ajusta la búsqueda para encontrar resultados
                              </Typography>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  component="div"
                  count={filteredSections.length}
                  page={pageSections}
                  onPageChange={handleChangePageSections}
                  rowsPerPage={rowsPerPageSections}
                  onRowsPerPageChange={handleChangeRowsPerPageSections}
                  labelRowsPerPage="Filas por página:"
                  labelDisplayedRows={({ from, to, count }) =>
                    `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
                  }
                  rowsPerPageOptions={[5, 10, 25]}
                />
              </Paper>
            </Grid>

            <Grid item xs={12} lg={4}>
              <Paper
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  boxShadow: "0 10px 30px rgba(15,23,42,0.06)",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: { xs: "flex-start", md: "center" },
                    justifyContent: "space-between",
                    gap: 2,
                    mb: 2,
                    flexWrap: "wrap",
                  }}
                >
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: 19, color: "#0f172a" }}>
                      Marcas
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: "#64748b", mt: 0.4 }}>
                      Listado alfabético de marcas registradas
                    </Typography>
                  </Box>

                  <Chip
                    label={`${filteredBrands.length} marcas`}
                    color="info"
                    variant="outlined"
                    sx={{ fontWeight: 700, borderRadius: 2 }}
                  />
                </Box>

                <TextField
                  fullWidth
                  placeholder="Buscar marcas"
                  value={searchTermBrands}
                  onChange={(e) => setSearchTermBrands(e.target.value)}
                  sx={{ mb: 2.2 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />

                <TableContainer
                  sx={{
                    borderRadius: 2,
                    border: "1px solid #e2e8f0",
                    overflowX: "auto",
                    overflowY: "hidden",
                    maxWidth: "100%",
                    "&::-webkit-scrollbar": { height: 10 },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "#cbd5e1",
                      borderRadius: 10,
                    },
                  }}
                >
                  <Table size="small" sx={{ minWidth: 320 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ ...headCellSx, minWidth: 240 }}>
                          Nombre
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {filteredBrands
                        .slice(
                          pageBrands * rowsPerPageBrands,
                          pageBrands * rowsPerPageBrands + rowsPerPageBrands
                        )
                        .map((brand) => (
                          <TableRow
                            key={brand.id}
                            hover
                            sx={{
                              "& td": { py: 1.1, borderBottom: "1px solid #edf2f7" },
                              "&:hover": { bgcolor: "#fafcff" },
                            }}
                          >
                            <TableCell sx={{ minWidth: 240 }}>
                              <Typography
                                sx={{
                                  fontSize: 13,
                                  fontWeight: 600,
                                  color: "#0f172a",
                                }}
                              >
                                {brand.name}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}

                      {!filteredBrands.length && (
                        <TableRow>
                          <TableCell align="center" sx={{ py: 5 }}>
                            <Stack spacing={0.75} alignItems="center">
                              <Typography sx={{ fontWeight: 700, color: "#0f172a" }}>
                                No hay marcas para mostrar
                              </Typography>
                              <Typography sx={{ fontSize: 13, color: "#64748b" }}>
                                Ajusta la búsqueda para encontrar resultados
                              </Typography>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  component="div"
                  count={filteredBrands.length}
                  page={pageBrands}
                  onPageChange={handleChangePageBrands}
                  rowsPerPage={rowsPerPageBrands}
                  onRowsPerPageChange={handleChangeRowsPerPageBrands}
                  labelRowsPerPage="Filas por página:"
                  labelDisplayedRows={({ from, to, count }) =>
                    `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
                  }
                  rowsPerPageOptions={[5, 10, 25]}
                />
              </Paper>
            </Grid>
          </Grid>

          <Modal
            open={openImageModal}
            onClose={handleCloseImageModal}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{ timeout: 250 }}
          >
            <Fade in={openImageModal}>
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: { xs: "92%", sm: "78%", md: "62%", lg: "48%" },
                  maxHeight: "90vh",
                  bgcolor: "#fff",
                  borderRadius: 3,
                  boxShadow: "0 24px 60px rgba(15,23,42,0.28)",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    px: 2.2,
                    py: 1.6,
                    borderBottom: "1px solid #e2e8f0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  <Typography sx={{ fontWeight: 700, color: "#0f172a" }}>
                    {modalImageTitle}
                  </Typography>

                  <Button
                    onClick={handleCloseImageModal}
                    variant="outlined"
                    size="small"
                    sx={{ textTransform: "none", fontWeight: 700 }}
                  >
                    Cerrar
                  </Button>
                </Box>

                <Box
                  sx={{
                    p: 2,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    bgcolor: "#f8fafc",
                  }}
                >
                  <Box
                    component="img"
                    src={modalImageUrl}
                    alt={modalImageTitle}
                    sx={{
                      maxWidth: "100%",
                      maxHeight: "75vh",
                      objectFit: "contain",
                      borderRadius: 2,
                    }}
                  />
                </Box>
              </Box>
            </Fade>
          </Modal>
        </Box>
      </Box>
    </Box>
  );
};

export default AdminSection;